'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download } from 'lucide-react';

type Member = {
  id: string;
  name: string;
  daysAvailable: number;
  focusPct: number; // 0-100
};

const STORAGE_KEY = 'planetroadmap.sprint-capacity-calculator.v1';

function newMember(name = ''): Member {
  return {
    id: Math.random().toString(36).slice(2),
    name,
    daysAvailable: 10,
    focusPct: 70,
  };
}

function defaults(): Member[] {
  return [
    { ...newMember('Alex'), daysAvailable: 10, focusPct: 75 },
    { ...newMember('Sam'), daysAvailable: 8, focusPct: 70 },
    { ...newMember('Jordan'), daysAvailable: 10, focusPct: 60 },
    { ...newMember('Taylor'), daysAvailable: 5, focusPct: 50 },
  ];
}

export function SprintCapacityCalculator() {
  const [members, setMembers] = useState<Member[]>([]);
  const [pointsPerDay, setPointsPerDay] = useState(2);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { members: Member[]; pointsPerDay: number };
        if (parsed && Array.isArray(parsed.members) && parsed.members.length > 0) {
          setMembers(parsed.members);
          setPointsPerDay(parsed.pointsPerDay || 2);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setMembers(defaults());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ members, pointsPerDay }));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [members, pointsPerDay, hydrated]);

  function update(id: string, patch: Partial<Member>) {
    setMembers((curr) => curr.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }
  function remove(id: string) {
    setMembers((curr) => curr.filter((m) => m.id !== id));
  }
  function add() {
    setMembers((curr) => [...curr, newMember()]);
  }
  function reset() {
    if (!confirm('Reset to default team?')) return;
    setMembers(defaults());
    setPointsPerDay(2);
  }

  const stats = useMemo(() => {
    const focusedDays = members.reduce(
      (acc, m) => acc + (m.daysAvailable * m.focusPct) / 100,
      0,
    );
    const rawDays = members.reduce((acc, m) => acc + m.daysAvailable, 0);
    const points = focusedDays * pointsPerDay;
    return { focusedDays, rawDays, points };
  }, [members, pointsPerDay]);

  function exportCsv() {
    const rows = [
      ['Member', 'Days available', 'Focus %', 'Focused days'],
      ...members.map((m) => [
        m.name || 'Unnamed',
        m.daysAvailable,
        m.focusPct,
        ((m.daysAvailable * m.focusPct) / 100).toFixed(1),
      ]),
      ['', '', '', ''],
      ['Total raw days', stats.rawDays.toFixed(1), '', ''],
      ['Total focused days', stats.focusedDays.toFixed(1), '', ''],
      ['Points per focused day', pointsPerDay, '', ''],
      ['Sprint capacity (points)', stats.points.toFixed(1), '', ''],
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sprint-capacity.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading calculator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Team members" value={String(members.length)} />
        <StatCard label="Raw days" value={stats.rawDays.toFixed(1)} />
        <StatCard label="Focused days" value={stats.focusedDays.toFixed(1)} accent />
        <StatCard label="Sprint capacity" value={`${stats.points.toFixed(0)} pts`} accent />
      </div>

      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6 space-y-2">
        <label className="text-sm font-semibold flex items-center gap-3 flex-wrap">
          Story points per focused day:
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={pointsPerDay}
            onChange={(e) => setPointsPerDay(Math.max(0.1, Number(e.target.value)))}
            className="w-20 text-right border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <span className="text-xs font-normal text-muted-foreground">
            ← derive this from past sprints (velocity ÷ focused days). 1.5–2.5 is typical.
          </span>
        </label>
      </div>

      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground min-w-[200px]">
                  Member
                </th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Days available
                  <div className="text-[10px] font-normal text-muted-foreground/70">
                    sprint days minus PTO/holidays
                  </div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">
                  Focus %
                  <div className="text-[10px] font-normal text-muted-foreground/70">
                    after meetings/support
                  </div>
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">Focused days</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => {
                const focused = (m.daysAvailable * m.focusPct) / 100;
                return (
                  <tr key={m.id} className={i < members.length - 1 ? 'border-b border-border/40' : ''}>
                    <td className="py-2 px-4">
                      <input
                        type="text"
                        value={m.name}
                        onChange={(e) => update(m.id, { name: e.target.value })}
                        placeholder="Name"
                        className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={m.daysAvailable}
                        onChange={(e) =>
                          update(m.id, { daysAvailable: Math.max(0, Number(e.target.value)) })
                        }
                        className="w-20 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={5}
                        value={m.focusPct}
                        onChange={(e) =>
                          update(m.id, { focusPct: Math.max(0, Math.min(100, Number(e.target.value))) })
                        }
                        className="w-20 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                      />
                    </td>
                    <td className="py-2 px-4 text-right font-mono font-semibold text-primary">
                      {focused.toFixed(1)}
                    </td>
                    <td className="py-2 px-2">
                      <button
                        onClick={() => remove(m.id)}
                        aria-label="Remove member"
                        className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {members.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-muted-foreground">
                    No team members yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add member
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={members.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl p-4 ${
        accent ? 'bg-primary/5 border border-primary/20' : 'bg-white dark:bg-card border border-border/60'
      }`}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? 'text-primary' : ''}`}>{value}</div>
    </div>
  );
}
